import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Dialog as MDialog,
  DialogContent,
  IconButton,
  Paper,
  DialogTitle,
  DialogActions,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import { FHButton, FProgressBar } from '../../../base';
import { sessionActions } from '../../../../store';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY4,
  FORTA_GRAY3,
  FORTA_SUCCESS,
} from '../../../../utils/colors';
import { getUtcOffset } from '../../../../utils/common';
import { areUserRolesIn, history } from '../../../../helpers';
import ScoreSection from './ScoreSection';
import CommentsSection from './CommentsSection';
import { Roles, BTSessionStatus } from '../../../../constants';
import { CQAReviewStatus, SessionEvalSchemaVersion } from '../constants';
import { getCQAReviewPayload } from '../../../../pages/session/btSessionNotes/utils';

import type { IClinicalQAReview, IClinicalQAReviewDiscourse } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { ResizableBoxProps } from 'react-resizable';
import type { DialogProps as MDialogProps, PaperProps } from '@mui/material';
import type { ClinicalQAScoreField } from './config';

interface ReviewDialogProps extends MDialogProps {
  clinicalQAReview: IClinicalQAReview;
  sessionStatus: BTSessionStatus;
}

enum SectionSteps {
  score,
  comment,
}

const DIALOG_MAX_WIDTH = 877;
const DIALOG_MIN_WIDTH = 442;
const DIALOG_HEIGHT = 442;
const DOUBLE_COLUMN_LIMIT_WIDTH = 544;

const ScoreFieldsV1: ClinicalQAScoreField[] = [
  'is_datetime_accurate',
  'is_data_collected_per_forta_protocols',
  'is_language_objective',
  'is_note_submitted_within_48hours_of_session',
  'has_10data_points_per_hour',
  'is_at_least_2programs_run_per_hour',
  'is_all_active_programs_used_within_7days',
  'is_detail_sufficient_quantity',
  'is_detail_sufficient_quality',
  'is_overall_coherent',
];

const ScoreFieldsV2: ClinicalQAScoreField[] = [
  'is_datetime_accurate',
  'one_setting_per_session_note',
  'is_language_objective',
  'is_note_submitted_within_48hours_of_session',
  'has_10data_points_per_hour',
  'is_at_least_2programs_run_per_hour',
  'is_all_active_programs_used_within_7days',
  'therapist_role_aligns_with_tp_goals',
  'is_detail_sufficient_quality',
  'is_overall_coherent',
];

const StyledDialog = styled(MDialog)<MDialogProps>(() => ({
  '& .MuiDialog-paper': {
    borderRadius: '10px',
    overflow: 'hidden',
    border: `1px solid ${FORTA_BLUE.light2}`,
    boxShadow: '8px 8px 8px 0px rgba(74, 74, 74, 0.25)',
    position: 'absolute',
    right: 10,
    top: 90,

    '& .MuiDialogContent-root': {
      '&>.MuiBox-root': {
        height: '100%',
      },
    },
  },
}));

const StyledResizableBox = styled(ResizableBox)<ResizableBoxProps>(() => ({
  position: 'relative',
  maxWidth: '100%',

  '& .react-resizable-handle': {
    display: 'block',
    position: 'absolute',
    width: 16,
    height: 16,
  },
  '& .react-resizable-handle-sw': {
    bottom: -4,
    left: -4,
    cursor: 'sw-resize',
  },
  '& .react-resizable-handle-se': {
    bottom: -4,
    right: -4,
    cursor: 'se-resize',
  },
  '& .react-resizable-handle-nw': {
    top: -4,
    left: -4,
    cursor: 'nw-resize',
  },
  '& .react-resizable-handle-ne': {
    top: -4,
    right: -4,
    cursor: 'ne-resize',
  },
  '& .react-resizable-handle-w': {
    cursor: 'ew-resize',
    left: -4,
    top: 12,
    height: 'calc(100% - 24px)',
  },
  '& .react-resizable-handle-e': {
    top: 12,
    right: -4,
    height: 'calc(100% - 24px)',
    cursor: 'ew-resize',
  },
}));

const StyledButton = styled(FHButton)(() => ({
  background: 'transparent',
  color: FORTA_GRAY4,
  border: `1px solid ${FORTA_BLUE.light2}`,

  '&:hover': {
    background: 'transparent',
    color: FORTA_GRAY4,
    border: `1px solid ${FORTA_BLUE.light2}`,
  },
}));

const PaperComponent = (props: PaperProps) => {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds={'parent'}
    >
      <Paper {...props} />
    </Draggable>
  );
};

function ReviewDialog(props: ReviewDialogProps) {
  const { clinicalQAReview, sessionStatus, open, onClose, ...rest } = props;
  const { id: sessionId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const { reviewLoading, current: session } = useSelector((state: RootState) => state.sessions);
  const [reviewData, setReviewData] = useState<IClinicalQAReview>(clinicalQAReview);
  const [step, setStep] = useState<SectionSteps>(SectionSteps.score);
  const [showOneColumn, setShowOneColumn] = useState<boolean>(false);
  const [waitForPatchCallAndSubmit, setWaitForPatchCallAndSubmit] = useState<boolean>(false);

  const isReviewProvidedByLoggedInUser =
    clinicalQAReview?.reviewing_provider?.email === user?.email;

  const ScoreFields =
    clinicalQAReview.eval_schema_version === SessionEvalSchemaVersion.V2
      ? ScoreFieldsV2
      : ScoreFieldsV1;

  const isEditable = useMemo(() => {
    if (!reviewData) return false;

    return (
      sessionStatus === BTSessionStatus.AwaitingReview &&
      reviewData.status !== CQAReviewStatus.AwaitingAcknowledgement
    );
  }, [reviewData, sessionStatus]);

  const isScoreEditable = useMemo(() => {
    if (!reviewData) return false;

    return (
      sessionStatus === BTSessionStatus.AwaitingReview &&
      reviewData.status === CQAReviewStatus.InProgress
    );
  }, [reviewData, sessionStatus]);

  const latestComment = useMemo(() => {
    if (!reviewData?.discourses) return;

    return reviewData.discourses.find(
      (discourse: IClinicalQAReviewDiscourse) => !discourse.date_submitted,
    );
  }, [reviewData?.discourses]);

  useEffect(() => {
    if (!clinicalQAReview) return;

    const latest = clinicalQAReview.discourses.find(
      (discourse: IClinicalQAReviewDiscourse) => !discourse.date_submitted,
    );
    if (latest) {
      setReviewData(clinicalQAReview);
    } else {
      // if this is not initial review(re-review) then add the empty discourse
      setReviewData({
        ...clinicalQAReview,
        discourses: [
          ...clinicalQAReview.discourses,
          {
            is_edit_requested: false,
            comment: null,
            date_submitted: null,
            utc_offset_date_submitted: null,
          },
        ],
      });
    }
  }, [clinicalQAReview]);

  const allScoresValued = useMemo(() => {
    let existingNullField = false;
    ScoreFields.forEach((key: ClinicalQAScoreField) => {
      if (reviewData[key] === null) {
        existingNullField = true;
      }
    });

    return !existingNullField;
  }, [reviewData]);

  const status = useMemo(() => {
    if (!reviewData.score) {
      return {
        color: FORTA_GRAY3,
        value: 'status',
      };
    }

    if (reviewData.score < 80) {
      return {
        color: FORTA_ERROR,
        value: 'Fail',
      };
    }

    return {
      color: FORTA_SUCCESS,
      value: 'Pass',
    };
  }, [reviewData.score]);

  useEffect(() => {
    if (waitForPatchCallAndSubmit && !reviewLoading) {
      setWaitForPatchCallAndSubmit(false);
      handleSubmit();
    }
  }, [reviewLoading, waitForPatchCallAndSubmit]);

  const handleResize = (event: any, { node: _node, size, handle: _handle }: any) => {
    if (size.width < DOUBLE_COLUMN_LIMIT_WIDTH) {
      setShowOneColumn(true);
      return;
    }

    setShowOneColumn(false);
  };

  const handleClose = () => {
    setShowOneColumn(false);
    setStep(SectionSteps.score);
    onClose?.({}, 'backdropClick');
  };

  const handleUpdateScore = (data: IClinicalQAReview) => {
    let score = 0;
    ScoreFields.forEach((key: ClinicalQAScoreField) => {
      if (data[key]) {
        score += 10;
      }
    });
    const updatedData = { ...data, score };
    setReviewData(updatedData);
    if (isReviewProvidedByLoggedInUser) {
      handleSaveInprogressCQAReview(updatedData);
    }
  };

  const handleSubmit = async () => {
    if (!sessionId || !reviewData) return;

    if (reviewLoading) {
      setWaitForPatchCallAndSubmit(true);
      return;
    }

    if (latestComment?.is_edit_requested === null) {
      toast.error('Please select whether edits are required for the session note');
      return;
    }

    if (latestComment?.is_edit_requested && !latestComment.comment) {
      toast.error('Please provide a comment');
      return;
    }

    if (
      latestComment?.is_edit_requested &&
      latestComment.comment &&
      latestComment.comment?.length < 4
    ) {
      toast.error('Comment requires at least 4 characters');
      return;
    }

    // if it's initial review, we need to call PATCH /clinical_qa_review
    // else if it's re-review, we need to call POST /discourses
    // Don't forget that only the first review needs acknowledge
    const isInitialReview = reviewData.discourses.length === 1 ? true : false;
    if (isInitialReview) {
      const initialReviewRes = await handleSubmitInitialReview();
      if (!initialReviewRes || initialReviewRes.error) return;
    } else {
      // Re-review
      const rereviewRes = await handleSubmitReReview();
      if (!rereviewRes || rereviewRes.error) return;
    }
    const toastId = 'sessionReviewSubmittedToast';
    toast.success('Session review submitted.', { toastId });
    handleClose();
  };

  const handleSaveInprogressCQAReview = (updatedReviewData: IClinicalQAReview) => {
    dispatch(
      sessionActions.updateCQAReview({
        sessionId,
        data: getCQAReviewPayload(
          updatedReviewData,
          clinicalQAReview?.eval_schema_version ?? SessionEvalSchemaVersion.V1,
          updatedReviewData.discourses.find(
            (discourse: IClinicalQAReviewDiscourse) => !discourse.date_submitted,
          ),
        ),
        getAccessTokenSilently,
      }),
    );
  };

  const handleSubmitInitialReview = async () => {
    const updateRes = await dispatch(
      sessionActions.updateCQAReview({
        sessionId,
        data: getCQAReviewPayload(
          reviewData,
          clinicalQAReview?.eval_schema_version ?? SessionEvalSchemaVersion.V1,
          latestComment,
        ),
        getAccessTokenSilently,
      }),
    );

    if (!updateRes || updateRes.error) return;

    const stopRes = await dispatch(
      sessionActions.stopCQAReview({
        sessionId,
        reviewId: reviewData.id,
        data: {
          date_reviewed: moment(new Date()),
          utc_offset_date_reviewed: getUtcOffset(moment(new Date())),
        },
        getAccessTokenSilently,
      }),
    );

    return stopRes;
  };

  const handleSubmitReReview = async () => {
    const res = await dispatch(
      sessionActions.discourseCQAReview({
        sessionId,
        reviewId: reviewData.id,
        data: {
          is_edit_requested: latestComment?.is_edit_requested,
          comment: latestComment?.comment === '' ? null : latestComment?.comment,
          date_submitted: moment(new Date()),
          utc_offset_date_submitted: getUtcOffset(moment(new Date())),
        },
        getAccessTokenSilently,
      }),
    );

    return res;
  };

  const showAcknowledgmentButton =
    areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) &&
    session?.id &&
    session.status === BTSessionStatus.EditsRequested &&
    session.clinical_qa_review?.status !== CQAReviewStatus.Acknowledged;

  return (
    <StyledDialog
      data-testid="sessionReviewDialog"
      {...rest}
      maxWidth={false}
      PaperComponent={PaperComponent}
      slots={{
        backdrop: () => null,
      }}
      open={open}
      onClose={handleClose}
    >
      {reviewLoading && <FProgressBar />}
      <StyledResizableBox
        height={DIALOG_HEIGHT}
        width={DIALOG_MAX_WIDTH}
        minConstraints={[DIALOG_MIN_WIDTH, DIALOG_HEIGHT]}
        maxConstraints={[DIALOG_MAX_WIDTH, DIALOG_HEIGHT]}
        resizeHandles={['sw', 'se', 'nw', 'ne', 'w', 'e']}
        axis="both"
        onResize={handleResize}
      >
        <Box
          sx={{ display: 'flex', flex: 1, flexDirection: 'column', width: '100%', height: '100%' }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'move',
              fontSize: 24,
              fontWeight: 700,
              padding: '0.5em 1em',
            }}
            id="draggable-dialog-title"
          >
            Session note review
            <IconButton sx={{ color: FORTA_BLUE.primary }} disableRipple onClick={handleClose}>
              <DoDisturbOnOutlinedIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 1, pb: 1 }}>
            {step === SectionSteps.score && (
              <ScoreSection
                gradingVersion={
                  clinicalQAReview?.eval_schema_version ?? SessionEvalSchemaVersion.V1
                }
                reviewData={reviewData}
                isOneColumn={showOneColumn}
                isEditable={isScoreEditable}
                disabled={reviewLoading}
                allScoresValued={allScoresValued}
                onUpdate={(data: IClinicalQAReview) => handleUpdateScore(data)}
              />
            )}
            {step === SectionSteps.comment && (
              <CommentsSection
                reviewData={reviewData}
                isEditable={isEditable}
                disabled={reviewLoading}
                onUpdate={(data: IClinicalQAReview) => setReviewData(data)}
                handleSave={(data: IClinicalQAReview) => {
                  handleSaveInprogressCQAReview(data);
                }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', p: 3 }}>
            {step === SectionSteps.score && (
              <>
                {!areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) && (
                  <Box display={'flex'} flexGrow={1} flexDirection={'column'}>
                    <Typography fontWeight={700}>Current score</Typography>
                    <Box data-testid="currentQaScore" display={'flex'} alignItems={'center'}>
                      <CircleIcon
                        data-testid={status.color}
                        sx={{ width: 10, height: 10, mr: 1, color: status.color }}
                      />
                      <Typography>
                        {reviewData.score === 0 ? '-' : reviewData.score} /100 - {status.value}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {showAcknowledgmentButton && (
                  <FHButton
                    disabled={reviewLoading}
                    onClick={() => {
                      if (!session?.id) return;
                      history.navigate(`/sessions/${session?.id}/review`);
                    }}
                  >
                    Acknowledge
                  </FHButton>
                )}
                <Box display={'flex'} flexGrow={1} justifyContent={'flex-end'}>
                  <StyledButton
                    disabled={reviewLoading}
                    data-testid={isEditable ? 'qaScoreNextButton' : 'qaScoreSessionComments'}
                    onClick={() => setStep(SectionSteps.comment)}
                    endIcon={<ArrowForwardIosIcon />}
                  >
                    {isEditable ? 'Next' : 'Review'}
                  </StyledButton>
                </Box>
              </>
            )}
            {step === SectionSteps.comment && (
              <>
                {isEditable ? (
                  <>
                    <StyledButton
                      disabled={reviewLoading}
                      data-testid="previous"
                      onClick={() => setStep(SectionSteps.score)}
                    >
                      Previous
                    </StyledButton>
                    <FHButton
                      data-testid={
                        latestComment?.is_edit_requested ? 'submitReviewButton' : 'finalizeButton'
                      }
                      hidden={areUserRolesIn(user?.roles, [Roles.Prbt])}
                      disabled={!isEditable || !allScoresValued}
                      onClick={() => handleSubmit()}
                    >
                      {latestComment?.is_edit_requested ? 'Submit Review' : 'Finalize'}
                    </FHButton>
                  </>
                ) : (
                  <Box display={'flex'} flexGrow={1} justifyContent={'flex-end'}>
                    <StyledButton
                      disabled={reviewLoading}
                      data-testid="sessionScore"
                      onClick={() => setStep(SectionSteps.score)}
                      startIcon={<ArrowBackIosNewIcon />}
                    >
                      Session score
                    </StyledButton>
                  </Box>
                )}
              </>
            )}
          </DialogActions>
        </Box>
      </StyledResizableBox>
    </StyledDialog>
  );
}

export default ReviewDialog;
